import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../App';
import api, { getErrorMessage } from '../utils/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, Bed, Users, Star, Plus, PencilSimple, Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ClientTrackingModal from '../components/ClientTrackingModal';
import EditBatchModal from '../components/EditBatchModal';
import AIGuideDrawer from '../components/AIGuideDrawer';
import { RequestEditControl } from '../components/EditRequests';

// Convert 24-hour time to 12-hour AM/PM format
const convertTo12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const PremiumSchedule = () => {
  const { activeBranch } = useBranch();
  const { user } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showClientTracking, setShowClientTracking] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBatchTime, setSelectedBatchTime] = useState(null);
  const [showEditBatch, setShowEditBatch] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // AI Guide states
  const [showAIGuide, setShowAIGuide] = useState(false);
  const [selectedGuideCustomer, setSelectedGuideCustomer] = useState(null);
  const [editAccess, setEditAccess] = useState({});

  const fetchEditAccess = async () => {
    if (!activeBranch?.branch_id) return;
    try {
      const res = await api.get('/edit-requests/mine', { params: { branch_id: activeBranch.branch_id } });
      setEditAccess(res.data || {});
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchEditAccess(); const t = setInterval(fetchEditAccess, 15000); return () => clearInterval(t); }, [activeBranch]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestEdit = async (batch, bedData) => {
    try {
      const res = await api.post('/edit-requests', {
        customer_id: bedData.customer_id,
        batch_id: batch.batch_id,
        batch_time: batch.start_time,
        batch_type: 'premium',
        branch_id: activeBranch?.branch_id,
      });
      toast.success(res.data.message);
      fetchEditAccess();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send request'));
    }
  };
  
  // Check if user is admin or owner
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner';

  const handleGenerateBatches = async () => {
    try {
      setGenerating(true);
      const response = await api.post('/batches/generate-today', {
        branch_id: activeBranch?.branch_id
      });
      toast.success(response.data.message || 'Premium batches generated successfully');
      fetchSchedule(); // Refresh the schedule
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate premium batches'));
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (activeBranch) {
      fetchSchedule();
      // Auto-refresh every 15 seconds
      const interval = setInterval(fetchSchedule, 15000);
      return () => clearInterval(interval);
    }
  }, [activeBranch]);

  const fetchSchedule = async () => {
    try {
      const response = await api.get('/batches/premium-schedule', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setSchedule(response.data.batches || []);
    } catch (error) {
      toast.error('Failed to load premium schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleBedClick = (batch, bedNum) => {
    const bedData = batch.bed_layout[bedNum];
    if (!bedData) return; // Empty bed, do nothing
    
    // Allow viewing both active and completed sessions
    setSelectedCustomer({
      customer_id: bedData.customer_id,
      name: bedData.customer_name,
      reg_no: bedData.customer_reg_no,
      bed_number: bedNum
    });
    setSelectedBatchTime(batch.start_time);
    setShowClientTracking(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading premium schedule...</div>
      </Layout>
    );
  }

  const totalCapacity = schedule.reduce((sum, b) => sum + b.capacity, 0);
  const totalOccupied = schedule.reduce((sum, b) => sum + b.occupied, 0);

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#2C4C3B] flex items-center gap-2">
              <Star size={32} weight="fill" className="text-amber-500" />
              Premium Schedule
            </h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">
              5-bed schedule exclusively for Purchased customers
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {isAdminOrOwner && (
              <Button
                onClick={handleGenerateBatches}
                disabled={generating}
                className="bg-amber-600 hover:bg-amber-700 text-white text-sm"
              >
                <Plus size={18} className="mr-2" />
                {generating ? 'Generating...' : 'Generate 16 Premium Batches'}
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                Premium Only
              </Badge>
              <span className="text-xs sm:text-sm text-[#1C1C1A]/60">
                Auto-refresh: 15s
              </span>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <Clock size={20} className="text-amber-700 sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-amber-700/70">Total Batches</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-900">{schedule.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <Bed size={20} className="text-green-700 sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-green-700/70">Total Capacity</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">{totalCapacity}</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <Users size={20} className="text-blue-700 sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-blue-700/70">Occupied</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">{totalOccupied}</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <Bed size={20} className="text-purple-700 sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-purple-700/70">Available</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-900">
                  {totalCapacity - totalOccupied}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Batch Cards */}
        <div className="space-y-3 sm:space-y-4">
          {schedule.map((batch) => {
            const alertClass = batch.alert_level === 'full' ? 'border-red-400 bg-red-50' :
                              batch.alert_level === 'warning' ? 'border-amber-400 bg-amber-50' :
                              batch.is_current ? 'border-green-400 bg-green-50' :
                              'border-[#E5E5DF]';
            
            return (
              <Card key={batch.batch_id} className={`p-3 sm:p-4 ${alertClass} transition-all`}>
                {/* Batch Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3">
                  <div className="flex items-center gap-2 flex-1">
                    <Clock size={18} className="text-[#2C4C3B] sm:w-5 sm:h-5" weight="bold" />
                    <h3 className="text-base sm:text-lg font-semibold text-[#2C4C3B]">
                      {convertTo12Hour(batch.start_time)} - {convertTo12Hour(batch.end_time)}
                    </h3>
                    {batch.is_current && (
                      <Badge className="bg-green-600 text-white text-xs">Active</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      <span className="font-medium text-[#2C4C3B]">
                        {batch.occupied}/{batch.capacity} Occupied
                      </span>
                      <span className="text-[#1C1C1A]/60">
                        ({batch.occupancy_percent}%)
                      </span>
                    </div>
                    {isAdminOrOwner && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBatch(batch);
                          setShowEditBatch(true);
                        }}
                        className="border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white"
                      >
                        <PencilSimple size={16} className="mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                {/* Bed Grid - Dynamic based on capacity */}
                <div className="grid grid-cols-5 gap-2">
                  {[...Array(batch.capacity || 5)].map((_, idx) => {
                    const bedNum = idx + 1;
                    const bedData = batch.bed_layout[bedNum];
                    const isEmpty = !bedData;
                    const isCompleted = bedData?.completed;
                    
                    return (
                      <div
                        key={idx}
                        className={`
                          p-2 sm:p-3 rounded-lg border-2 transition-all
                          ${isEmpty 
                            ? 'bg-white border-gray-200 opacity-60' 
                            : isCompleted
                            ? 'bg-gray-100 border-gray-300'
                            : 'bg-amber-50 border-amber-400 hover:shadow-md'
                          }
                        `}
                      >
                        <div className="text-xs font-bold text-[#2C4C3B] mb-1">
                          Bed {bedNum}
                        </div>
                        {bedData && (
                          <>
                            <div 
                              onClick={() => (!isCompleted || editAccess[bedData.customer_id] === 'approved') && handleBedClick(batch, bedNum)}
                              className={(!isCompleted || editAccess[bedData.customer_id] === 'approved') ? 'cursor-pointer' : ''}
                            >
                              <div className="text-xs sm:text-sm font-semibold text-[#1C1C1A] truncate">
                                {bedData.customer_name}
                              </div>
                              <div className="text-xs text-[#1C1C1A]/60 truncate">
                                {bedData.token_number}
                              </div>
                              {isCompleted && (
                                <div className="text-xs text-gray-500 mt-1">Done</div>
                              )}
                            </div>
                            {isCompleted && (
                              <RequestEditControl
                                status={editAccess[bedData.customer_id]}
                                onRequest={() => requestEdit(batch, bedData)}
                                onOpen={() => handleBedClick(batch, bedNum)}
                              />
                            )}
                            {!isEmpty && !isCompleted && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedGuideCustomer({ 
                                    id: bedData.customer_id, 
                                    name: bedData.customer_name 
                                  });
                                  setShowAIGuide(true);
                                }}
                                className="w-full mt-2 bg-teal-500 hover:bg-teal-600 text-white text-xs py-1 h-auto"
                              >
                                <Robot size={12} weight="fill" className="mr-1" />
                                AI Guide
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>

        {schedule.length === 0 && (
          <Card className="p-8 text-center">
            <Star size={48} weight="thin" className="mx-auto text-[#1C1C1A]/20 mb-4" />
            <p className="text-lg text-[#1C1C1A]/60">No premium batches found</p>
            <p className="text-sm text-[#1C1C1A]/40 mt-2">
              Premium batches will be created automatically
            </p>
          </Card>
        )}

        {/* Client Tracking Modal */}
        {showClientTracking && selectedCustomer && (
          <ClientTrackingModal
            open={showClientTracking}
            onClose={() => {
              setShowClientTracking(false);
              setSelectedCustomer(null);
              fetchSchedule();
              fetchEditAccess();
            }}
            customer={selectedCustomer}
            batchTime={selectedBatchTime}
            branchId={activeBranch?.branch_id}
          />
        )}

        {/* Edit Batch Modal */}
        <EditBatchModal
          open={showEditBatch}
          onClose={() => {
            setShowEditBatch(false);
            setSelectedBatch(null);
          }}
          batch={selectedBatch}
          onSuccess={() => {
            fetchSchedule();
          }}
        />

        {/* AI Guide Drawer */}
        <AIGuideDrawer
          isOpen={showAIGuide}
          onClose={() => {
            setShowAIGuide(false);
            setSelectedGuideCustomer(null);
          }}
          customerId={selectedGuideCustomer?.id}
          customerName={selectedGuideCustomer?.name}
        />
      </div>
    </Layout>
  );
};

export default PremiumSchedule;
